import { useApolloClient } from "@apollo/client";
import React from "react";
import { GET_PERSON_RELATIONSHIPS } from "src/components/graphql/queries";
import { useCreateFamily } from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import type { NewStudentEntry, StudentEntry } from "./useStudentEntries";

export type ParentAddress = {
  street_address: string | null;
  street_address_line_2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
};

interface UseAddStudentsToParentParams {
  organizationId: string;
  parentId: string;
  parentAddress: ParentAddress;
}

/**
 * Links staged students (existing by id, or newly created) to an existing parent
 * via createFamily: no parent is created, each student gets a parent -> student
 * relationship, and the relationships query is refreshed.
 */
export function useAddStudentsToParent({
  organizationId,
  parentId,
  parentAddress,
}: UseAddStudentsToParentParams) {
  const toast = useAvelaToast();
  const { mutateAsync: createFamily } = useCreateFamily();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const submit = async (entries: StudentEntry[]): Promise<boolean> => {
    if (entries.length === 0) return false;
    setIsSubmitting(true);
    try {
      const students = entries
        .filter((entry): entry is NewStudentEntry => entry.kind === "new")
        .map((entry) => ({
          id: entry.id,
          first_name: entry.firstName,
          middle_name: entry.middleName,
          last_name: entry.lastName,
          birth_date: entry.birthDate,
          reference_id: entry.referenceId,
          ...(entry.useParentAddress
            ? parentAddress
            : {
                street_address: entry.streetAddress,
                street_address_line_2: entry.streetAddressLine2,
                city: entry.city,
                state: entry.state,
                zip_code: entry.zipCode,
              }),
        }));
      // Parent is first_person, each student is second_person.
      const relationships = entries.map((entry) => ({
        first_person: parentId,
        second_person: entry.id,
      }));

      await createFamily({
        body: {
          data: { organizationId, students, guardians: [], relationships },
        },
      });
      await apolloClient.refetchQueries({
        include: [GET_PERSON_RELATIONSHIPS],
      });
      toast({
        title: "Student added",
        status: "success",
        isClosable: true,
      });
      return true;
    } catch (error) {
      console.error(error);
      toast.error({ title: "Error adding student" });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { submit, isSubmitting };
}
